<script lang="ts">
    import { Input } from '@/components/ui/input';
    import { Loader2 } from 'lucide-svelte';
    import { onMount } from 'svelte';

    type Address = {
        line_1: string;
        line_2: string;
        city: string;
        postcode: string;
        county: string;
    };

    type Props = {
        onAddressSelect: (address: Address) => void;
        label?: string;
        placeholder?: string;
    };

    let { onAddressSelect, label = "Find Address", placeholder = "Start typing an address..." }: Props = $props();

    let searchQuery = $state('');
    let addresses = $state<Address[]>([]);
    let isLoading = $state(false);
    let isOpen = $state(false);
    let error = $state<string | null>(null);
    let apiKey = $state<string | null>(null);
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    // Close dropdown when clicking outside
    let dropdownRef: HTMLDivElement;

    $effect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
                isOpen = false;
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    });

    onMount(() => {
        // Load Geoapify API key from environment
        // Get your free API key from: https://www.geoapify.com/
        apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY || null;

        if (!apiKey) {
            error = "Geoapify API key not configured. Please add VITE_GEOAPIFY_API_KEY to your .env file.";
        }
    });

    // Debounced search effect
    $effect(() => {
        if (searchQuery.length < 3) {
            addresses = [];
            isOpen = false;
            return;
        }

        // Clear existing timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        // Set new timer
        debounceTimer = setTimeout(() => {
            searchAddresses();
        }, 300); // 300ms debounce

        // Cleanup
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    });

    async function searchAddresses() {
        if (!searchQuery.trim() || !apiKey) {
            return;
        }

        isLoading = true;
        error = null;
        addresses = [];

        try {
            // Use Geoapify Address Autocomplete API
            // Filter for UK addresses only
            const params = new URLSearchParams({
                text: searchQuery,
                apiKey: apiKey,
                filter: 'countrycode:gb', // UK only
                format: 'json',
                bias: 'proximity:50.906792,-3.482973', // Bias towards Tiverton
                limit: '7' // Limit to 5 results
            });

            const response = await fetch(`https://api.geoapify.com/v1/geocode/autocomplete?${params}`); // Bias towards Tiverton

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    error = "Invalid API key. Please check your configuration.";
                } else {
                    error = "Failed to lookup address";
                }
                return;
            }

            const data = await response.json();
            // Parse Geoapify response
            if (data.results && Array.isArray(data.results)) {
                addresses = data.results.map((address: any) => {
                    return {
                        line_1: address.housenumber && address.street
                            ? `${address.housenumber} ${address.street}`
                            : address.street || address.address_line1 || '',
                        line_2: address.address_line2 || '',
                        city: address.city || '',
                        postcode: address.postcode || '',
                        county: address.county || address.state || ''
                    };
                });

                if (addresses.length > 0) {
                    isOpen = true;
                } else {
                    error = "No addresses found. Try a different search.";
                }
            }

        } catch (err) {
            console.error('Address lookup error:', err);
            error = "Failed to lookup address. Please try again.";
        } finally {
            isLoading = false;
        }
    }

    function selectAddress(address: Address) {
        onAddressSelect(address);
        isOpen = false;
        // Show full address in the input after selection
        const displayAddress = [
            address.line_1,
            address.city,
            address.postcode
        ].filter(Boolean).join(', ');
       // searchQuery = displayAddress;
    }
</script>

<div class="address-lookup" bind:this={dropdownRef}>
    <label class="block text-sm font-medium mb-2">
        {label}
    </label>

    <div class="search-container">
        <div class="input-with-spinner">
            <Input
                type="text"
                bind:value={searchQuery}
                {placeholder}
                class="pr-10"
            />
            {#if isLoading}
                <div class="spinner-container">
                    <Loader2 class="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
            {/if}
        </div>

        {#if error}
            <p class="error-message">{error}</p>
        {/if}

        {#if isOpen && addresses.length > 0}
            <div class="dropdown">
                <div class="dropdown-header">
                    Select an address:
                </div>
                {#each addresses as address, index (index)}
                    <button
                        type="button"
                        class="dropdown-item"
                        onclick={() => selectAddress(address)}
                    >
                        <div class="address-line">{address.line_1}</div>
                        {#if address.line_2}
                            <div class="address-line-secondary">{address.line_2}</div>
                        {/if}
                        <div class="address-line-secondary">
                            {address.city}, {address.county} {address.postcode}
                        </div>
                    </button>
                {/each}
            </div>
        {/if}
    </div>

    <p class="text-xs text-muted-foreground mt-1">
        Start typing an address or postcode (min 3 characters)
    </p>
</div>

<style lang="scss">
    .address-lookup {
        position: relative;
        width: 100%;
        grid-column: span 2;
    }

    .search-container {
        position: relative;
    }

    .input-with-spinner {
        position: relative;
        width: 100%;
    }

    .spinner-container {
        position: absolute;
        right: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
    }

    .error-message {
        color: var(--destructive);
        font-size: 0.875rem;
        margin-top: 0.5rem;
    }

    .dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 0.25rem;
        background: var(--popover);
        border: 1px solid var(--border);
        border-radius: 8px;
        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        z-index: 50;
        max-height: 300px;
        overflow-y: auto;
    }

    .dropdown-header {
        padding: 0.75rem 1rem;
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--muted-foreground);
        border-bottom: 1px solid var(--border);
    }

    .dropdown-item {
        width: 100%;
        text-align: left;
        padding: 0.75rem 1rem;
        border: none;
        background: transparent;
        cursor: pointer;
        transition: background 150ms;

        &:hover {
            background: var(--accent);
        }

        &:not(:last-child) {
            border-bottom: 1px solid var(--border);
        }
    }

    .address-line {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--foreground);
        margin-bottom: 0.25rem;
    }

    .address-line-secondary {
        font-size: 0.75rem;
        color: var(--muted-foreground);
    }
</style>
